import {
  PROFILE_MAGIC_TAG_KEYS,
  readCanonicalTagValue,
} from "@/lib/profileMagicTagKeys";
import type { ProfileMagicTagRow } from "@/lib/profileMagicReadiness";

/** Same JSON shape for ลูกค้าประจำ / ชื่อเรียก and คู่ค้าบ่อย from receipt */
export type RegularCustomerAliasEntry = {
  name: string;
  alias?: string;
  /** 13-digit tax ID when known */
  tax_id?: string;
};

export function parseRegularCustomerAliases(
  tags: ProfileMagicTagRow[],
): RegularCustomerAliasEntry[] {
  const raw = readCanonicalTagValue(
    tags,
    PROFILE_MAGIC_TAG_KEYS.customerAliases,
  );
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const o = item as Record<string, unknown>;
        const name = String(o.name ?? "").trim();
        if (!name) return null;
        const alias = o.alias != null ? String(o.alias).trim() : "";
        const taxRaw = o.tax_id != null ? String(o.tax_id) : "";
        const taxDigits = taxRaw.replace(/\D/g, "");
        return {
          name,
          ...(alias ? { alias } : {}),
          ...(taxDigits ? { tax_id: taxDigits } : {}),
        } satisfies RegularCustomerAliasEntry;
      })
      .filter((e): e is RegularCustomerAliasEntry => e != null);
  } catch {
    return [];
  }
}

export function formatRegularCustomerLine(entry: RegularCustomerAliasEntry): string {
  const parts = [entry.name.trim()];
  if (entry.alias?.trim()) parts.push(entry.alias.trim());
  if (entry.tax_id?.trim()) parts.push(entry.tax_id.replace(/\D/g, ""));
  return parts.join(", ");
}

export function regularCustomersToText(
  entries: RegularCustomerAliasEntry[],
): string {
  return entries.map(formatRegularCustomerLine).join("\n");
}

export function parseRegularCustomersText(raw: string): RegularCustomerAliasEntry[] {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(",").map((s) => s.trim());
      const name = parts[0] ?? line;
      const alias = parts[1] || undefined;
      const taxDigits = (parts[2] ?? "").replace(/\D/g, "");
      return {
        name,
        ...(alias ? { alias } : {}),
        ...(taxDigits ? { tax_id: taxDigits } : {}),
      };
    });
}

/** Merge one entry (e.g. คู่ค้าบ่อย from receipt) into list by tax_id or name */
export function mergeRegularCustomerEntry(
  existing: RegularCustomerAliasEntry[],
  entry: RegularCustomerAliasEntry,
): RegularCustomerAliasEntry[] {
  const name = entry.name.trim();
  if (!name) return existing;

  const taxDigits = entry.tax_id?.replace(/\D/g, "") ?? "";
  const normalized: RegularCustomerAliasEntry = {
    name,
    ...(entry.alias?.trim() ? { alias: entry.alias.trim() } : {}),
    ...(taxDigits ? { tax_id: taxDigits } : {}),
  };

  const idx = existing.findIndex((e) => {
    if (taxDigits && e.tax_id?.replace(/\D/g, "") === taxDigits) return true;
    return e.name.trim().toLowerCase() === name.toLowerCase();
  });

  if (idx >= 0) {
    const next = [...existing];
    next[idx] = { ...next[idx], ...normalized };
    return next;
  }
  return [...existing, normalized];
}

export function partnerEntryFromReceiptBuyer(input: {
  name: string;
  legalName: string;
  taxId: string;
}): RegularCustomerAliasEntry | null {
  const legal = input.legalName.trim();
  const display = input.name.trim();
  const name = legal || display;
  if (!name) return null;

  const taxDigits = input.taxId.replace(/\D/g, "");
  const alias =
    legal && display && legal !== display ? display : undefined;

  return {
    name,
    ...(alias ? { alias } : {}),
    ...(taxDigits ? { tax_id: taxDigits } : {}),
  };
}
